import React from 'react'
import PropTypes from 'prop-types'

import {
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Stack,
    Text,
    Image
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useSegment from '../../integrations/segment/app/hooks/use-segment'
import {productUrlBuilder} from '../../utils/url'

// Component Skeleton
export const Skeleton = () => {
    return (
        <Box data-testid="sf-product-tile-skeleton" className="product-block">
            <Box className="product-tile" maxWidth="300px" margin="0 auto">
                <Stack spacing={2}>
                    <Box>
                        <AspectRatio ratio={1}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    </Box>
                    <ChakraSkeleton width="80%" height={{base: '30px', lg: '20px'}} />
                    <ChakraSkeleton width={'30%'} height="24px" />
                    <ChakraSkeleton width="100%" height={'10'} borderRadius={'8px'} mb={2} />
                </Stack>
            </Box>
        </Box>
    )
}

/**
 * The RecentlyViewed is a simple visual representation of a
 * product object. It will show it's default image, name.
 */
const ProductTileRecentlyViewed = (props) => {
    const segment = useSegment()
    const {
        product,
        productPosition,
        // Destructure the props causing warnings so they aren't spread to DOM elements
        ...rest
    } = props

    const {image} = product

    // RecentlyViewed is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName = product.name ?? product.productName

    const getImageSrc = () => {
        const baseUrl =
            image?.disBaseLink || image?.link || product?.image?.disBaseLink || product?.image?.link
        // Validate base URL exists and is properly formatted
        if (!baseUrl || typeof baseUrl !== 'string') {
            return null
        }
        return `${baseUrl}?sw=200&q=60`
    }

    return (
        <Box className="product-block">
            <Box className="product-tile">
                <Box className={'product-tile-image-block'}>
                    <Link
                        data-testid="product-tile"
                        to={productUrlBuilder(product)}
                        onClick={(event) => {
                            segment.sendProductClick(product, productPosition, event)
                            segment.sendProductViewed(event, product)
                            segment.sendPanelSelectedEvent(event, product)
                        }}
                        data-position={productPosition}
                        {...rest}
                        display="block"
                    >
                        <AspectRatio
                            ratio={1}
                            display="flex"
                            alignItems="center"
                            className="image-container"
                        >
                            <Image
                                data-testid="product-tile-image"
                                borderTopLeftRadius="8px"
                                borderTopRightRadius="8px"
                                src={getImageSrc()}
                                alt={`${localizedProductName}`}
                                loading="lazy"
                                onClick={(event) => {
                                    if (event.type !== 'click') return
                                    segment.sendProductClick(product, productPosition, event)
                                    segment.sendProductViewed(event, product)
                                    segment.sendPanelSelectedEvent(event, product)
                                }}
                            />
                        </AspectRatio>
                        <Text
                            noOfLines={2}
                            fontSize={'12px'}
                            fontWeight={500}
                            lineHeight={'20px'}
                            margin={{base: '8px', md: '12px'}}
                            h={'40px'}
                        >
                            {localizedProductName}
                        </Text>
                    </Link>
                </Box>
            </Box>
        </Box>
    )
}

ProductTileRecentlyViewed.displayName = 'ProductTileRecentlyViewed'

ProductTileRecentlyViewed.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        name: PropTypes.string,
        productName: PropTypes.string,
        productId: PropTypes.string
    }),
    productPosition: PropTypes.number,
    dynamicImageProps: PropTypes.object
}

export default ProductTileRecentlyViewed
